import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RotateCcw } from "lucide-react";
import type { PrivacyConfig } from "../types";

interface PrivacySettingsProps {
  settings: PrivacyConfig;
  onChange: (settings: PrivacyConfig) => void;
}

export const PrivacySettings = ({ settings, onChange }: PrivacySettingsProps) => {
  const handleChange = (field: keyof PrivacyConfig, value: string | boolean) => {
    onChange({
      ...settings,
      [field]: value,
    });
  };

  const handleResetCookieConsent = () => {
    localStorage.removeItem("cookieConsent");
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new Event('cookieConsentReset'));
    toast.success("Cookie consent has been reset");
  };

  return (
    <div className="space-y-6">
      <Card className="p-4">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="privacyEnabled" className="text-base font-medium">
              Enable Privacy Policy & Cookie Consent
            </Label>
            <Switch
              id="privacyEnabled"
              checked={settings.enabled}
              onCheckedChange={(checked) => handleChange("enabled", checked)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="privacyTitle">Privacy Policy Title</Label>
            <Input
              id="privacyTitle"
              value={settings.title}
              onChange={(e) => handleChange("title", e.target.value)}
              placeholder="Privacy Policy"
              disabled={!settings.enabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="privacyContent">Privacy Policy Content</Label>
            <Textarea
              id="privacyContent"
              value={settings.content}
              onChange={(e) => handleChange("content", e.target.value)}
              placeholder="Enter your privacy policy content here..."
              className="min-h-[200px]"
              disabled={!settings.enabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="cookieText">Cookie Consent Message</Label>
            <Textarea
              id="cookieText"
              value={settings.cookieConsentText}
              onChange={(e) => handleChange("cookieConsentText", e.target.value)}
              placeholder="We use cookies to improve your experience..."
              disabled={!settings.enabled}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="acceptText">Accept Button Text</Label>
              <Input
                id="acceptText"
                value={settings.acceptButtonText}
                onChange={(e) => handleChange("acceptButtonText", e.target.value)}
                placeholder="Accept"
                disabled={!settings.enabled}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="learnMoreText">Learn More Button Text</Label>
              <Input
                id="learnMoreText"
                value={settings.learnMoreButtonText}
                onChange={(e) => handleChange("learnMoreButtonText", e.target.value)}
                placeholder="Learn More"
                disabled={!settings.enabled}
              />
            </div>
          </div>

          <div className="pt-4 border-t">
            <Button
              variant="outline"
              onClick={handleResetCookieConsent}
              disabled={!settings.enabled}
              className="w-full sm:w-auto"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset Cookie Consent
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};